import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { TenantSummary } from '@marque/api/central'

function StatusBadge({ status }: { status: string }) {
    const colours: Record<string, string> = {
        Active: 'bg-green-100 text-green-800',
        Suspended: 'bg-yellow-100 text-yellow-800',
        PendingBootstrap: 'bg-blue-100 text-blue-800',
        Archived: 'bg-neutral-100 text-neutral-600',
    }
    const cls = colours[status] ?? 'bg-neutral-100 text-neutral-600'
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
            {status}
        </span>
    )
}

function TenantRow({ tenant }: { tenant: TenantSummary }) {
    return (
        <tr className="hover:bg-neutral-50">
            <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                <Link
                    to="/tenants/$slug"
                    params={{ slug: tenant.slug }}
                    className="hover:text-blue-600"
                >
                    {tenant.displayName}
                </Link>
            </td>
            <td className="px-4 py-3 text-sm text-neutral-500 font-mono">{tenant.slug}</td>
            <td className="px-4 py-3 text-sm">
                <div className="flex items-center gap-1.5">
                    <StatusBadge status={tenant.status} />
                    {!tenant.isSetupComplete && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            Setup pending
                        </span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3 text-sm text-neutral-500">{tenant.provisioningMode}</td>
            <td className="px-4 py-3 text-sm text-neutral-500">{tenant.adminEmail}</td>
            <td className="px-4 py-3 text-sm text-neutral-400">
                {new Date(tenant.createdAt).toLocaleDateString()}
            </td>
        </tr>
    )
}

export function TenantsPage() {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['tenants'],
        queryFn: () => centralApi.listTenants(),
    })

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-neutral-900">Tenants</h1>
                <Link
                    to="/tenants/new"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                    New tenant
                </Link>
            </div>

            {isLoading && (
                <p className="text-neutral-500 text-sm">Loading…</p>
            )}

            {isError && (
                <p className="text-red-600 text-sm">Failed to load tenants.</p>
            )}

            {data && (
                <>
                    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-neutral-200">
                            <thead className="bg-neutral-50">
                                <tr>
                                    {['Display name', 'Slug', 'Status', 'Mode', 'Admin email', 'Created'].map(h => (
                                        <th
                                            key={h}
                                            className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {data.items.map(tenant => (
                                    <TenantRow key={tenant.tenantId} tenant={tenant} />
                                ))}
                                {data.items.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-4 py-8 text-center text-sm text-neutral-400"
                                        >
                                            No tenants yet.{' '}
                                            <Link to="/tenants/new" className="text-blue-600 hover:underline">
                                                Create the first one.
                                            </Link>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-3 text-xs text-neutral-400 text-right">
                        {data.totalCount} tenant{data.totalCount !== 1 ? 's' : ''}
                    </p>
                </>
            )}
        </div>
    )
}
